import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  userLocationConnectorAPI, 
  powerLocationAPI,
  inverterConfigAPI,
  temperatureConfigAPI,
  panelConfigAPI,
  showApiError 
} from '../../services/api';
import './PowerPlantInquiry.css';

const PowerPlantInquiry = () => {
  const [inquiryList, setInquiryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const isInitialLoad = useRef(true);

  // 장비 정보
  const [inverters, setInverters] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [panels, setPanels] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  // 수정 모드
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPowerLocation, setEditedPowerLocation] = useState(null);

  // 장비 수정 모드
  const [editingEquipment, setEditingEquipment] = useState(null); // { type: 'inverter'|'sensor'|'panel', id: number, data: object }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { type: string, id: number, name: string }

  // 발전소 조회 목록 로드
  const loadInquiryList = useCallback(async (reset = false) => {
    if (loading) return;
    
    console.log('loadInquiryList called - reset:', reset, 'loading:', loading);
    setLoading(true);
    try {
      const condition = {
        keyword: searchKeyword,
        cursor: reset ? null : cursor,
        limit: 20,
        sortBy: 'serialCode',
        sortDirection: 'DESC'
      };
      
      console.log('API call condition:', condition);
      const response = await userLocationConnectorAPI.getAll(condition);
      
      console.log('PowerPlantInquiry - API response:', response);
      console.log('PowerPlantInquiry - Data:', response.data);
      
      if (reset) {
        console.log('Resetting inquiry list with:', response.data);
        setInquiryList(response.data || []);
      } else {
        console.log('Adding to existing inquiry list:', response.data);
        setInquiryList(prev => {
          const newData = [...prev, ...(response.data || [])];
          console.log('New inquiry list state:', newData);
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
    } catch (error) {
      console.error('발전소 조회 목록 로드 실패:', error);
      alert('발전소 조회 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, cursor]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadInquiryList();
      isInitialLoad.current = false;
    }
  }, [loadInquiryList]);

  // 검색
  const handleSearch = () => {
    setCursor(null);
    setHasMore(true);
    loadInquiryList(true);
  };

  // 엔터키 검색 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadInquiryList();
    }
  };

  // 장비 정보 로드
  const loadEquipmentInfo = async (powerLocationId) => {
    setLoadingEquipment(true);
    try {
      // 인버터 정보 로드
      const inverterData = await inverterConfigAPI.getInverterList(powerLocationId);
      setInverters(inverterData || []);

      // 온도 센서 정보 로드
      try {
        const sensorData = await temperatureConfigAPI.getTemperatureList(powerLocationId);
        // 백엔드가 List<TemperatureConfigListDto>를 직접 반환
        setSensors(Array.isArray(sensorData) ? sensorData : []);
      } catch (error) {
        console.log('온도 센서 정보 없음:', error);
        setSensors([]);
      }

      // 접속반 정보 로드
      const panelData = await panelConfigAPI.getPanelList(powerLocationId);
      setPanels(panelData || []);
    } catch (error) {
      console.error('장비 정보 로드 실패:', error);
    } finally {
      setLoadingEquipment(false);
    }
  };

  // 상세보기
  const handleViewDetails = async (item) => {
    try {
      // 연결 정보 가져오기
      const detailedItem = await userLocationConnectorAPI.get(item.id);
      
      // 발전소 상세 정보를 별도로 가져오기
      let powerLocationDetail = detailedItem.powerLocation;
      if (detailedItem.powerLocation?.id) {
        try {
          powerLocationDetail = await powerLocationAPI.get(detailedItem.powerLocation.id);
        } catch (error) {
          console.error('발전소 상세 정보 로드 실패:', error);
          // 기본 정보라도 사용
        }
      }
      
      // 상세 정보 업데이트
      const updatedItem = {
        ...detailedItem,
        powerLocation: powerLocationDetail
      };
      
      setSelectedItem(updatedItem);
      setEditedPowerLocation({...powerLocationDetail});
      setIsEditMode(false);
      
      // 장비 정보 로드
      if (powerLocationDetail?.id) {
        await loadEquipmentInfo(powerLocationDetail.id);
      }
      
      setShowModal(true);
    } catch (error) {
      console.error('발전소 상세 정보 로드 실패:', error);
      showApiError(error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setEditedPowerLocation(null);
    setIsEditMode(false);
    setInverters([]);
    setSensors([]);
    setPanels([]);
  };

  // 수정 모드 토글
  const handleEditToggle = () => {
    if (isEditMode) {
      // 수정 취소
      setEditedPowerLocation({...selectedItem.powerLocation});
    }
    setIsEditMode(!isEditMode);
  };

  // 발전소 정보 수정 저장
  const handleSavePowerLocation = async () => {
    try {
      await powerLocationAPI.update(editedPowerLocation.id, editedPowerLocation);
      alert('발전소 정보가 성공적으로 수정되었습니다!');
      
      // 상세 정보 다시 로드
      const detailedItem = await userLocationConnectorAPI.get(selectedItem.id);
      
      // 발전소 상세 정보를 별도로 가져오기
      let powerLocationDetail = detailedItem.powerLocation;
      if (detailedItem.powerLocation?.id) {
        try {
          powerLocationDetail = await powerLocationAPI.get(detailedItem.powerLocation.id);
        } catch (error) {
          console.error('발전소 상세 정보 로드 실패:', error);
        }
      }
      
      // 상세 정보 업데이트
      const updatedItem = {
        ...detailedItem,
        powerLocation: powerLocationDetail
      };
      
      setSelectedItem(updatedItem);
      setEditedPowerLocation({...powerLocationDetail});
      setIsEditMode(false);
      
      // 목록도 새로고침
      loadInquiryList(true);
    } catch (error) {
      console.error('발전소 정보 수정 실패:', error);
      showApiError(error);
    }
  };

  // 입력 필드 변경 핸들러
  const handleInputChange = (field, value) => {
    setEditedPowerLocation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 장비 수정 시작
  const handleEditEquipment = (type, equipment) => {
    setEditingEquipment({ type, id: equipment.id, data: {...equipment} });
  };

  // 장비 수정 취소
  const handleCancelEditEquipment = () => {
    setEditingEquipment(null);
  };

  // 장비 수정 저장
  const handleSaveEquipment = async () => {
    if (!editingEquipment) return;

    try {
      const { type, id, data } = editingEquipment;
      
      if (type === 'inverter') {
        const updateData = {
          inverterDeviceName: data.inverterDeviceName,
          inverterId: data.inverterId,
          name: data.name,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout,
          capacity: data.capacity
        };
        await inverterConfigAPI.update(id, updateData);
        alert('인버터 정보가 수정되었습니다.');
      } else if (type === 'sensor') {
        const updateData = {
          name: data.name,
          sensorId: data.sensorId,
          protocolType: data.protocolType,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout
        };
        await temperatureConfigAPI.update(id, updateData);
        alert('온도 센서 정보가 수정되었습니다.');
      } else if (type === 'panel') {
        const updateData = {
          name: data.name,
          panelId: data.panelId,
          channel: data.channel,
          port: data.port,
          baudrate: data.baudrate,
          bytesize: data.bytesize,
          timeout: data.timeout
        };
        await panelConfigAPI.update(id, updateData);
        alert('접속반 정보가 수정되었습니다.');
      }

      // 장비 정보 다시 로드
      if (selectedItem?.powerLocation?.id) {
        await loadEquipmentInfo(selectedItem.powerLocation.id);
      }
      setEditingEquipment(null);
    } catch (error) {
      console.error('장비 정보 수정 실패:', error);
      showApiError(error);
    }
  };

  // 장비 삭제 확인
  const handleDeleteEquipmentConfirm = (type, id, name) => {
    setShowDeleteConfirm({ type, id, name });
  };

  // 장비 삭제 실행
  const handleDeleteEquipment = async () => {
    if (!showDeleteConfirm) return;

    try {
      const { type, id } = showDeleteConfirm;
      
      if (type === 'inverter') {
        await inverterConfigAPI.delete(id);
        alert('인버터가 삭제되었습니다.');
      } else if (type === 'sensor') {
        await temperatureConfigAPI.delete(id);
        alert('온도 센서가 삭제되었습니다.');
      } else if (type === 'panel') {
        await panelConfigAPI.delete(id);
        alert('접속반이 삭제되었습니다.');
      }

      // 장비 정보 다시 로드
      if (selectedItem?.powerLocation?.id) {
        await loadEquipmentInfo(selectedItem.powerLocation.id);
      }
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('장비 삭제 실패:', error);
      showApiError(error);
    }
  };

  // 장비 필드 변경
  const handleEquipmentFieldChange = (field, value) => {
    setEditingEquipment(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value
      }
    }));
  };

  return (
    <div className="inquiry-container">
      <div className="inquiry-header">
        <h2>🔍 발전소 조회</h2>
        <p>등록된 발전소와 연결된 사용자 및 장비 정보를 조회합니다. (관리자 전용)</p>
      </div>

      <div className="inquiry-content">
        <div className="inquiry-list-section">
          <div className="inquiry-list-header">
            <h3>발전소 목록</h3>
            <div className="inquiry-search-section">
              <input
                type="text"
                placeholder="시리얼 코드 또는 발전소명으로 검색"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="inquiry-search-input"
              />
              <button onClick={handleSearch} className="btn btn-primary btn-sm">
                검색
              </button>
            </div>
          </div>
          <div className="inquiry-table">
            <div className="inquiry-table-header">
              <div className="inquiry-table-cell">시리얼 코드</div>
              <div className="inquiry-table-cell">발전소 명</div>
              <div className="inquiry-table-cell">연결된 사용자</div>
              <div className="inquiry-table-cell">사용자 이름</div>
              <div className="inquiry-table-cell">연결 일시</div>
              <div className="inquiry-table-cell">관리</div>
            </div>
            {inquiryList.map(item => (
              <div key={item.id} className="inquiry-table-row">
                <div className="inquiry-table-cell">{item.powerLocation?.serialCode || '-'}</div>
                <div className="inquiry-table-cell">{item.powerLocation?.locationName || '-'}</div>
                <div className="inquiry-table-cell">{item.user?.username || '-'}</div>
                <div className="inquiry-table-cell">{item.user?.name || '-'}</div>
                <div className="inquiry-table-cell">
                  {item.createdAt ? new Date(item.createdAt).toLocaleString('ko-KR') : '-'}
                </div>
                <div className="inquiry-table-cell">
                  <div className="inquiry-action-buttons">
                    <button 
                      className="btn btn-info btn-sm"
                      onClick={() => handleViewDetails(item)}
                    >
                      상세보기
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* 무한스크롤 로딩 */}
            {loading && (
              <div className="inquiry-loading-row">
                <div className="inquiry-loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {/* 더 보기 버튼 */}
            {hasMore && !loading && (
              <div className="inquiry-load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {/* 더 이상 데이터가 없을 때 */}
            {!hasMore && inquiryList.length > 0 && (
              <div className="inquiry-no-more-data">
                <span>모든 데이터를 불러왔습니다.</span>
              </div>
            )}

            {/* 데이터가 없을 때 */}
            {!loading && inquiryList.length === 0 && (
              <div className="inquiry-no-more-data">
                <span>조회된 데이터가 없습니다.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 발전소 상세 모달 */}
      {showModal && selectedItem && editedPowerLocation && (
        <div className="inquiry-modal-overlay">
          <div className="inquiry-modal-content">
            <div className="inquiry-modal-header">
              <h3>발전소 상세 정보</h3>
              <button className="inquiry-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="inquiry-modal-body">
              <div className="inquiry-info">
                {/* 발전소 정보 그룹 */}
                <div className="inquiry-info-section">
                  <div className="inquiry-section-header">
                    <h4>발전소 정보</h4>
                    <button 
                      className={`btn btn-sm ${isEditMode ? 'btn-secondary' : 'btn-warning'}`}
                      onClick={handleEditToggle}
                    >
                      {isEditMode ? '취소' : '수정'}
                    </button>
                  </div>
                  <div className="inquiry-info-grid">
                    <div className="inquiry-info-item">
                      <label>시리얼 코드:</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedPowerLocation.serialCode || ''}
                          onChange={(e) => handleInputChange('serialCode', e.target.value)}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.serialCode || '-'}</span>
                      )}
                    </div>
                    <div className="inquiry-info-item">
                      <label>발전소 명:</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedPowerLocation.locationName || ''}
                          onChange={(e) => handleInputChange('locationName', e.target.value)}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.locationName || '-'}</span>
                      )}
                    </div>
                    <div className="inquiry-info-item">
                      <label>설비 용량 (kW):</label>
                      {isEditMode ? (
                        <input
                          type="number"
                          value={editedPowerLocation.capacity || ''}
                          onChange={(e) => handleInputChange('capacity', parseFloat(e.target.value))}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.capacity ? `${selectedItem.powerLocation.capacity}kW` : '-'}</span>
                      )}
                    </div>
                    <div className="inquiry-info-item full-width">
                      <label>주소:</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={editedPowerLocation.location || ''}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.location || '-'}</span>
                      )}
                    </div>
                    <div className="inquiry-info-item">
                      <label>위도:</label>
                      {isEditMode ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={editedPowerLocation.latitude || ''}
                          onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.latitude || '-'}</span>
                      )}
                    </div>
                    <div className="inquiry-info-item">
                      <label>경도:</label>
                      {isEditMode ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={editedPowerLocation.longitude || ''}
                          onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
                          className="inquiry-input"
                        />
                      ) : (
                        <span>{selectedItem.powerLocation?.longitude || '-'}</span>
                      )}
                    </div>
                  </div>
                  {isEditMode && (
                    <div className="inquiry-edit-actions">
                      <button 
                        className="btn btn-success"
                        onClick={handleSavePowerLocation}
                      >
                        저장
                      </button>
                    </div>
                  )}
                </div>

                {/* 인버터 정보 */}
                {loadingEquipment ? (
                  <div className="inquiry-loading-equipment">장비 정보 로딩 중...</div>
                ) : (
                  <>
                    {inverters.length > 0 && (
                      <div className="inquiry-info-section">
                        <h4>인버터 목록 ({inverters.length}개)</h4>
                        <div className="inquiry-equipment-list">
                          {inverters.map((inverter) => {
                            const isEditing = editingEquipment?.type === 'inverter' && editingEquipment?.id === inverter.id;
                            const editData = isEditing ? editingEquipment.data : inverter;
                            
                            return (
                              <div key={inverter.id} className="inquiry-equipment-item">
                                <div className="inquiry-equipment-header">
                                  <strong>{isEditing ? (
                                    <input
                                      type="text"
                                      value={editData.name || ''}
                                      onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                      className="inquiry-input-inline"
                                    />
                                  ) : (inverter.name || '인버터')}</strong>
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span className="inquiry-equipment-type">인버터</span>
                                    {!isEditing && (
                                      <>
                                        <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('inverter', inverter)}>수정</button>
                                        <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('inverter', inverter.id, inverter.name)}>삭제</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="inquiry-equipment-details">
                                  <div className="inquiry-detail-row">
                                    <span>RTU 인덱스:</span>
                                    <span>{inverter.rtuIdx ?? '-'}</span>
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>장치명:</span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editData.inverterDeviceName || ''}
                                        onChange={(e) => handleEquipmentFieldChange('inverterDeviceName', e.target.value)}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.inverterDeviceName || '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>인버터 ID:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.inverterId ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('inverterId', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.inverterId ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>포트:</span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editData.port || ''}
                                        onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.port || '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>보드레이트:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.baudrate ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.baudrate ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>바이트 크기:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.bytesize ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.bytesize ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>타임아웃:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={editData.timeout ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.timeout ?? '-'}s</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>용량:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={editData.capacity ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('capacity', parseFloat(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{inverter.capacity ?? '-'}kW</span>
                                    )}
                                  </div>
                                </div>
                                {isEditing && (
                                  <div className="inquiry-equipment-actions">
                                    <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                                    <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 온도 센서 정보 */}
                    {sensors.length > 0 && (
                      <div className="inquiry-info-section">
                        <h4>온도 센서 목록 ({sensors.length}개)</h4>
                        <div className="inquiry-equipment-list">
                          {sensors.map((sensor) => {
                            const isEditing = editingEquipment?.type === 'sensor' && editingEquipment?.id === sensor.id;
                            const editData = isEditing ? editingEquipment.data : sensor;
                            
                            return (
                              <div key={sensor.id} className="inquiry-equipment-item">
                                <div className="inquiry-equipment-header">
                                  <strong>{isEditing ? (
                                    <input
                                      type="text"
                                      value={editData.name || ''}
                                      onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                      className="inquiry-input-inline"
                                    />
                                  ) : (sensor.name || '온도센서')}</strong>
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span className="inquiry-equipment-type">온도센서</span>
                                    {!isEditing && (
                                      <>
                                        <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('sensor', sensor)}>수정</button>
                                        <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('sensor', sensor.id, sensor.name)}>삭제</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="inquiry-equipment-details">
                                  <div className="inquiry-detail-row">
                                    <span>RTU 인덱스:</span>
                                    <span>{sensor.rtuIdx ?? '-'}</span>
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>센서 ID:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.sensorId ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('sensorId', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.sensorId ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>프로토콜:</span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editData.protocolType || ''}
                                        onChange={(e) => handleEquipmentFieldChange('protocolType', e.target.value)}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.protocolType || '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>포트:</span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editData.port || ''}
                                        onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.port || '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>보드레이트:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.baudrate ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.baudrate ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>바이트 크기:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.bytesize ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.bytesize ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>타임아웃:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={editData.timeout ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{sensor.timeout ?? '-'}s</span>
                                    )}
                                  </div>
                                </div>
                                {isEditing && (
                                  <div className="inquiry-equipment-actions">
                                    <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                                    <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 접속반 정보 */}
                    {panels.length > 0 && (
                      <div className="inquiry-info-section">
                        <h4>접속반 목록 ({panels.length}개)</h4>
                        <div className="inquiry-equipment-list">
                          {panels.map((panel) => {
                            const isEditing = editingEquipment?.type === 'panel' && editingEquipment?.id === panel.id;
                            const editData = isEditing ? editingEquipment.data : panel;
                            
                            return (
                              <div key={panel.id} className="inquiry-equipment-item">
                                <div className="inquiry-equipment-header">
                                  <strong>{isEditing ? (
                                    <input
                                      type="text"
                                      value={editData.name || ''}
                                      onChange={(e) => handleEquipmentFieldChange('name', e.target.value)}
                                      className="inquiry-input-inline"
                                    />
                                  ) : (panel.name || '접속반')}</strong>
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span className="inquiry-equipment-type">접속반</span>
                                    {!isEditing && (
                                      <>
                                        <button className="btn btn-warning btn-xs" onClick={() => handleEditEquipment('panel', panel)}>수정</button>
                                        <button className="btn btn-danger btn-xs" onClick={() => handleDeleteEquipmentConfirm('panel', panel.id, panel.name)}>삭제</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="inquiry-equipment-details">
                                  <div className="inquiry-detail-row">
                                    <span>RTU 인덱스:</span>
                                    <span>{panel.rtuIdx ?? '-'}</span>
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>판넬 ID:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.panelId ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('panelId', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.panelId ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>채널:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.channel ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('channel', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.channel ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>포트:</span>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editData.port || ''}
                                        onChange={(e) => handleEquipmentFieldChange('port', e.target.value)}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.port || '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>보드레이트:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.baudrate ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('baudrate', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.baudrate ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>바이트 크기:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        value={editData.bytesize ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('bytesize', parseInt(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.bytesize ?? '-'}</span>
                                    )}
                                  </div>
                                  <div className="inquiry-detail-row">
                                    <span>타임아웃:</span>
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={editData.timeout ?? ''}
                                        onChange={(e) => handleEquipmentFieldChange('timeout', parseFloat(e.target.value))}
                                        className="inquiry-input-inline"
                                      />
                                    ) : (
                                      <span>{panel.timeout ?? '-'}s</span>
                                    )}
                                  </div>
                                </div>
                                {isEditing && (
                                  <div className="inquiry-equipment-actions">
                                    <button className="btn btn-success btn-sm" onClick={handleSaveEquipment}>저장</button>
                                    <button className="btn btn-secondary btn-sm" onClick={handleCancelEditEquipment}>취소</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* 연결된 사용자 정보 그룹 */}
                <div className="inquiry-info-section">
                  <h4>연결된 사용자 정보</h4>
                  <div className="inquiry-info-grid">
                    <div className="inquiry-info-item">
                      <label>사용자 ID:</label>
                      <span>{selectedItem.user?.username || '-'}</span>
                    </div>
                    <div className="inquiry-info-item">
                      <label>사용자 이름:</label>
                      <span>{selectedItem.user?.name || '-'}</span>
                    </div>
                    <div className="inquiry-info-item">
                      <label>사용자 역할:</label>
                      <span className={`inquiry-status-badge ${
                        selectedItem.user?.role === 'ADMIN' ? 'active' : 'pending'
                      }`}>
                        {selectedItem.user?.role || '-'}
                      </span>
                    </div>
                    <div className="inquiry-info-item">
                      <label>연락처:</label>
                      <span>{selectedItem.user?.phoneNumber || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* 연결 정보 그룹 */}
                <div className="inquiry-info-section">
                  <h4>연결 정보</h4>
                  <div className="inquiry-info-grid">
                    <div className="inquiry-info-item">
                      <label>연결 ID:</label>
                      <span>{selectedItem.id || '-'}</span>
                    </div>
                    <div className="inquiry-info-item">
                      <label>연결 일시:</label>
                      <span>{selectedItem.createdAt ? new Date(selectedItem.createdAt).toLocaleString('ko-KR') : '-'}</span>
                    </div>
                    <div className="inquiry-info-item">
                      <label>최종 수정 일시:</label>
                      <span>{selectedItem.updatedAt ? new Date(selectedItem.updatedAt).toLocaleString('ko-KR') : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="inquiry-modal-footer">
              <div className="inquiry-modal-footer-buttons">
                <button 
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 장비 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="inquiry-modal-overlay">
          <div className="inquiry-modal-content" style={{ maxWidth: '500px' }}>
            <div className="inquiry-modal-header">
              <h3>장비 삭제 확인</h3>
              <button className="inquiry-modal-close" onClick={() => setShowDeleteConfirm(null)}>×</button>
            </div>
            
            <div className="inquiry-modal-body">
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                  <strong>정말로 이 장비를 삭제하시겠습니까?</strong>
                </p>
                <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                  <p><strong>장비 유형:</strong> {showDeleteConfirm.type === 'inverter' ? '인버터' : showDeleteConfirm.type === 'sensor' ? '온도센서' : '접속반'}</p>
                  <p><strong>장비명:</strong> {showDeleteConfirm.name || '-'}</p>
                </div>
                <p style={{ color: '#dc3545', fontSize: '0.9rem' }}>이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>

            <div className="inquiry-modal-footer">
              <div className="inquiry-modal-footer-buttons">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  취소
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDeleteEquipment}
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerPlantInquiry;
